/* The Motherboard Module (Campaign Frame Materials, last page): the Ikonis, its augments and the
   scrap collection, copied from the PDF with punctuation normalized only. A scrap's number on the
   sheet is also what it sells for in Quantum. */

export const MOTHERBOARD = {
  trait: 'Choose a character trait.',
  range: 'Choose range and damage dice.',
  damageType: 'Choose damage type.',
  bonded: { name: 'Bonded', text: 'Gain a bonus to your damage rolls equal to your level.' },
  slots: 'Start with two slots. Unlock one at each tier.',
};

export const IKONIS_RANGES = [
  { range: 'Melee', damage: 'd12+1' },
  { range: 'Very Close', damage: 'd10+2' },
  { range: 'Close', damage: 'd10' },
  { range: 'Far', damage: 'd8+1' },
  { range: 'Very Far', damage: 'd6+1' },
];

export const IKONIS_DAMAGE_TYPES = ['Physical', 'Tech'];

export interface Augment {
  name: string;
  text: string;
  /** The printed scrap cost, read by augmentCost. */
  cost: string;
  /** Check boxes on the sheet: how many times it can be built. */
  boxes: number;
  precompile?: number;
}

export const AUGMENTS: Augment[] = [
  { name: 'Force', text: '+1 damage', cost: '3 gears, 2 lenses, 4 aluminum, 1 capacitor', boxes: 2 },
  { name: 'Guard', text: '+1 Armor Score', cost: '3 wires, 2 silver, 2 platinum, 3 fuses', boxes: 2 },
  { name: 'Converge', text: '+1 to attack rolls', cost: '4 coils, 2 crystals, 5 gold, 3 discs', boxes: 2 },
  { name: 'Amplify', text: 'On a successful attack, roll an additional damage die and drop the lowest result.', cost: '4 crystals, 4 cobalt, 4 copper, 4 capacitors', boxes: 1 },
  { name: 'Scope', text: 'Increase range by one step (Melee to Very Close, Close to Far, etc.).', cost: '5 lenses, 3 silver, 2 circuits, 2 relays', boxes: 2 },
  { name: 'Deny', text: '+2 Armor Score', cost: '6 coils, 3 wire, 2 copper, 4 batteries', boxes: 1 },
  { name: 'Target', text: '+2 to attack rolls', cost: '10 wires, 7 gold, 5 fuses, 5 circuits, 2 batteries', boxes: 1 },
  { name: 'Split', text: 'When you make an attack, mark a Stress to target another creature within range.', cost: '12 gears, 5 lenses, 15 aluminum, 9 relays', boxes: 1 },
  { name: 'Fix', text: 'When you deal Severe damage, clear a Hit Point.', cost: '6 coils, 4 wires, 1 crystal, 5 cobalt, 5 silver, 7 relays, 2 batteries', boxes: 2 },
  { name: 'Scare', text: 'When you critically succeed on an attack, the target must mark a Stress.', cost: '6 triggers, 8 copper, 9 aluminum, 10 discs', boxes: 2 },
  { name: 'Sear', text: '+2 damage', cost: '11 triggers, 11 platinum, 11 circuits, 7 discs', boxes: 2, precompile: 2 },
  { name: 'Absorb', text: 'You can mark an additional Armor Slot against incoming damage.', cost: '26 gears, 13 gold, 15 relays, 8 batteries', boxes: 1, precompile: 2 },
  { name: 'Kick', text: 'On a successful attack, you can mark 2 Stress to force the target to mark an additional Hit Point.', cost: '33 triggers, 13 crystals, 23 cobalt, 16 discs', boxes: 1, precompile: 2 },
  { name: 'Block', text: '+3 Armor Score; -1 Evasion', cost: '27 crystals, 67 aluminum, 33 relays, 4 capacitors, 5 batteries', boxes: 1, precompile: 3 },
  { name: 'Zip', text: 'Move up to Far range as part of an attack.', cost: '37 coils, 43 silver, 67 fuses, 12 capacitors', boxes: 1, precompile: 3 },
  { name: 'Bury', text: '+3 damage', cost: '28 triggers, 28 circuits, 28 relays, 1 relic', boxes: 2, precompile: 3 },
  { name: 'Follow', text: 'Mark 2 Stress to reroll your attack.', cost: '75 gears, 67 lenses, 30 copper, 33 circuits', boxes: 1, precompile: 4 },
  { name: 'Override', text: 'Attack rolls have advantage.', cost: '63 wires, 71 gold, 58 discs, 5 relics', boxes: 1, precompile: 4 },
];

/** The scrap collection, in the sheet's order; `value` is the number printed before each name. */
export const SCRAP = [
  { group: 'Shards', die: 'd6', items: [{ value: 1, name: 'Gear' }, { value: 2, name: 'Coil' }, { value: 3, name: 'Wire' }, { value: 4, name: 'Trigger' }, { value: 5, name: 'Lens' }, { value: 6, name: 'Crystal' }] },
  { group: 'Metals', die: 'd8', items: [{ value: 1, name: 'Aluminum' }, { value: 3, name: 'Copper' }, { value: 5, name: 'Cobalt' }, { value: 6, name: 'Silver' }, { value: 7, name: 'Platinum' }, { value: 8, name: 'Gold' }] },
  { group: 'Components', die: 'd10', items: [{ value: 1, name: 'Fuse' }, { value: 3, name: 'Circuit' }, { value: 6, name: 'Disc' }, { value: 8, name: 'Relay' }, { value: 9, name: 'Capacitor' }, { value: 10, name: 'Battery' }] },
];

/** From the Motherboard campaign frame (daggerheart.com), punctuation normalized. */
export const IKONIS_RULES = {
  weapon: 'Ikonis are always considered to be two-handed weapons and start with the "Bonded" feature.',
  augments:
    'A character may build as many augments as they\'d like, but they can only install a number equal to the Augment slots they have available. While installed, augments are treated as additional features for that weapon.',
  tether: 'Network tether',
};
